import React, { useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown, ChevronUp } from "lucide-react";
import { ActivityEventIcon, ToolActivityCard } from "@photon/chat-react/activity-timeline";
import ActivityCard from "./ActivityCard";
import IntentCard from "./IntentCard";
import PlanCard from "./PlanCard";
import ExecutionStepsCard from "./ExecutionStepsCard";
import ReasoningTraceCard from "./ReasoningTraceCard";
import { getActivityExecutionId, getActivityTitle, getDelegationDetails, getItemStyles } from "./activityItemUtils";

/**
 * TimelineItem - Individual activity item with dot, title, and content
 *
 * @param {Object} item - Activity item data
 * @param {number} index - Item index for animation delay
 * @param {boolean} isApprovalItem - Whether this is an approval outcome
 * @param {string} approvalStatus - "approved" | "cancelled" | "expired"
 * @param {boolean} isStreaming - Whether the parent message is still generating
 * @param {boolean} isActiveItem - Whether this is the current active (last) step during streaming
 * @param {boolean} expanded - External accordion control (omit to use internal state)
 * @param {Function} onToggle - External accordion toggle callback
 */
const TimelineItem = ({
  item,
  index,
  isApprovalItem = false,
  approvalStatus = null,
  isStreaming = false,
  isActiveItem = false,
  expanded: externalExpanded,
  onToggle,
}) => {
  const [localExpanded, setLocalExpanded] = useState(false);
  // Use external accordion control when provided; fall back to local state.
  const expanded = externalExpanded !== undefined ? externalExpanded : localExpanded;
  const setExpanded = onToggle
    ? () => onToggle()
    : (v) => setLocalExpanded(typeof v === "function" ? v(localExpanded) : v);

  const displayText = item?.description;
  const isToolItem = item?.type === "tool";
  const isDelegationItem = item?.type === "delegation";
  const isIntentItem = item?.type === "intent";
  const isPlanItem = item?.type === "plan";
  const isExecutionStepsItem = item?.type === "execution_steps";
  const isReasoningTraceItem = item?.type === "reasoning_trace";
  const executionId = getActivityExecutionId(item);
  const toolStatus = item?.status;
  const isRunningTool = isToolItem && toolStatus === 'running';
  const isUnresolvedTool = isToolItem && toolStatus === 'unresolved';

  // Approval-specific styling (not covered by shared getItemStyles).
  const getApprovalStyles = () => {
    switch (approvalStatus) {
      case "approved":
        return { dotColor: "bg-fill-green-500 dark:bg-fill-green-600", titleColor: "text-ink-green-700 dark:text-ink-green-400" };
      case "cancelled":
        return { dotColor: "bg-fill-gray-500 dark:bg-fill-gray-600", titleColor: "text-ink-gray-600 dark:text-ink-gray-400" };
      case "expired":
        return { dotColor: "bg-fill-hex-f97316 dark:bg-fill-orange-600", titleColor: "text-ink-orange-600 dark:text-ink-hex-fb923c" };
      default:
        return { dotColor: "bg-fill-hex-cccccc dark:bg-fill-muted-control", titleColor: "text-ink-blue-700 dark:text-ink-blue-400" };
    }
  };

  // Use shared color util; approval overrides take precedence.
  const sharedStyles = getItemStyles(item);
  const resolvedStyles = isApprovalItem ? getApprovalStyles() : { dotColor: sharedStyles.dot, titleColor: sharedStyles.title };
  const titleColor = resolvedStyles.titleColor;

  // Note: streaming/active state no longer affects content rendering. The parent
  // (ShowResearch) controls which item is expanded by default via `expandedIndex`.
  // The collapsed/expanded distinction is the only visual hierarchy needed.

  return (
    <motion.div
      key={index}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{
        delay: isStreaming ? 0 : index * 0.1,
        duration: 0.3,
        ease: "easeOut",
      }}
      className="relative pl-4 sm:pl-6"
    >
      <span
        className={`photon-activity-event-icon absolute -left-[7px] top-[1px] ${isRunningTool ? 'animate-pulse' : ''}`}
        data-status={isApprovalItem ? approvalStatus : item?.type === 'error' ? 'error' : toolStatus}
      >
        <ActivityEventIcon type={isApprovalItem ? 'approval' : item?.type} />
      </span>
      
      {/* Content — single rule:
          - Title row is always visible. Clicking it toggles `expanded` (when body exists).
          - Body is rendered iff `expanded`.
          - No streaming-specific branching: the parent (ShowResearch) controls which
            item is expanded by default via `expandedIndex`. */}
      <div className="rounded-lg">
        {/* Compute body once so the chevron and body share the same source of truth. */}
        {(() => {
          const body = isIntentItem ? (
            <IntentCard intents={item?.intents || []} steps={item?.steps || []} />
          ) : isPlanItem ? (
            <PlanCard content={item?.content} />
          ) : isExecutionStepsItem ? (
            <ExecutionStepsCard content={item?.content} />
          ) : isReasoningTraceItem ? (
            <ReasoningTraceCard step_summary={item?.step_summary} learn={item?.learn} />
          ) : isDelegationItem ? (
            <ActivityCard>
              <div className="space-y-3">
                {getDelegationDetails(item).map(({ label, value }) => (
                  <div key={label}>
                    <p className="text-[11px] font-semibold uppercase text-ink-gray-500 dark:text-ink-gray-400">{label}</p>
                    <p className="mt-1 whitespace-pre-line text-[13px] text-ink-hex-456175 dark:text-ink-hex-cccccc">{value}</p>
                  </div>
                ))}
              </div>
            </ActivityCard>
          ) : isToolItem ? (() => {
            return (
              <ToolActivityCard
                status={toolStatus}
                description={item?.description}
                code={item?.code}
                args={item?.args}
                sizeHint={item?.size_hint}
                resultPreview={item?.result_preview}
                stdout={item?.stdout}
                stderr={item?.stderr}
                cancellationLimitation={item?.cancellationLimitation || item?.cancellation_limitation}
              />
            );
          })() : (
            <ActivityCard>
              {displayText?.trim() || item?.title?.trim() || 'Processing...'}
            </ActivityCard>
          );

          // Whether this item has any body content worth expanding.
          const hasBody = isIntentItem
            ? !!(item?.intents?.length || item?.steps?.length)
            : isPlanItem || isExecutionStepsItem
              ? !!item?.content
              : isReasoningTraceItem
                ? !!(item?.step_summary || item?.learn)
                : isToolItem
                  ? !!(item?.args || item?.code || item?.stdout || item?.stderr || item?.size_hint || item?.result_preview || item?.description)
                  : isDelegationItem
                    ? getDelegationDetails(item).length > 0
                  : !!(displayText?.trim());

          // Keep actionable tool states visible while collapsed. Successful tools
          // already communicate completion through the checkmark.
          const collapsedToolStatus = isToolItem && !expanded ? (() => {
            if (!isRunningTool && !isUnresolvedTool && toolStatus !== "returned") return null;
            const label = isRunningTool
              ? "Running…"
              : isUnresolvedTool
                ? "Outcome unresolved"
                : "Returned";
            const color = isRunningTool
              ? "text-ink-primary dark:text-ink-hex-4f7aff animate-pulse"
              : isUnresolvedTool
                ? "text-ink-amber-700 dark:text-ink-amber-400"
                : "text-ink-gray-600 dark:text-ink-gray-300";
            return <p className={`text-[13px] mt-1 ${color}`}>{label}</p>;
          })() : null;

          return (
            <>
              {/* Title row — always visible, clickable when expandable */}
              <div
                className={`mb-[5px] flex items-center justify-between ${hasBody ? "cursor-pointer select-none" : ""}`}
                onClick={hasBody ? () => setExpanded((v) => !v) : undefined}
              >
                <div className="flex min-w-0 items-baseline gap-2">
                  <h3 className={`photon-activity-title text-[12px] uppercase font-bold ${titleColor}`}>
                    {getActivityTitle(item)}
                  </h3>
                  {item?.agent_name && !isDelegationItem && (
                    <span className="truncate text-[10px] font-medium text-ink-gray-500 dark:text-ink-gray-400">
                      {item.agent_name}
                      {executionId && (
                        <span className="font-normal italic text-ink-hex-aeb4bd dark:text-ink-gray-500"> ({executionId})</span>
                      )}
                    </span>
                  )}
                </div>
                {hasBody && (
                  <span className="text-ink-primary dark:text-ink-accent flex-shrink-0 ml-2">
                    {expanded ? <ChevronUp size={12} strokeWidth={2.5} /> : <ChevronDown size={12} strokeWidth={2.5} />}
                  </span>
                )}
              </div>

              {/* Compact status hint when a tool is collapsed */}
              {collapsedToolStatus}

              {/* Body */}
              {hasBody && expanded && body}
            </>
          );
        })()}
      </div>
    </motion.div>
  );
};

export default TimelineItem;
